import React, { useState, useEffect, useCallback } from "react";
import { X, Package, Plus, Loader, Search, ChevronRight } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const EnquiryBOMModal = ({
  isOpen,
  onClose,
  enquiryData,
  axiosInstance,
  onSuccess,
}) => {
  const [rawMaterials, setRawMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedCombination, setSelectedCombination] = useState(null);
  const [bomQuantity, setBomQuantity] = useState("");
  const [bomRemarks, setBomRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [warehouseStocks, setWarehouseStocks] = useState([]);
  const [selectedWarehouseStock, setSelectedWarehouseStock] = useState(null);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [bomHistory, setBomHistory] = useState(null);
  const [bomHistoryLoading, setBomHistoryLoading] = useState(false);
  const [bomHistoryError, setBomHistoryError] = useState("");
  const { user } = useAuth();

  const getApiEndpoint = useCallback(
    (path = "") => {
      const apiBase =
        user?.role === "PRODUCTION_MANAGER"
          ? process.env.REACT_APP_API_PROD_MANAGER
          : process.env.REACT_APP_API_OPERATIONS;
      return `${apiBase}${path}`;
    },
    [user?.role],
  );

  const fetchRawMaterials = useCallback(
    async (productId) => {
      if (!productId) return;
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(
          `${getApiEndpoint("")}/raw-materials/enabled/specs/${productId}`,
        );
        if (response.data?.status === 200 && response.data?.data) {
          setRawMaterials(response.data.data);
        }
      } catch (err) {
        setError("Failed to load raw materials");
        console.error("Error fetching raw materials:", err);
      } finally {
        setLoading(false);
      }
    },
    [axiosInstance, getApiEndpoint],
  );

  useEffect(() => {
    if (isOpen && enquiryData?.productHistories?.[0]?.productId) {
      fetchRawMaterials(enquiryData.productHistories[0].productId);
    }
  }, [isOpen, enquiryData, fetchRawMaterials]);

  const fetchBOMHistory = useCallback(
    async (productOrderHistoryId) => {
      if (!productOrderHistoryId) return;
      setBomHistoryLoading(true);
      setBomHistoryError("");
      try {
        const response = await axiosInstance.get(
          `${getApiEndpoint("")}/raw-material-stock/${productOrderHistoryId}`,
        );
        if (response.data?.data) {
          setBomHistory(response.data.data);
        } else {
          setBomHistory(null);
        }
      } catch (err) {
        setBomHistory(null);
        setBomHistoryError("Failed to load BOM stock history");
        console.error("Error fetching BOM history:", err);
      } finally {
        setBomHistoryLoading(false);
      }
    },
    [axiosInstance, getApiEndpoint],
  );

  useEffect(() => {
    if (isOpen && enquiryData?.productOrderHistoryId) {
      fetchBOMHistory(enquiryData.productOrderHistoryId);
    }
  }, [isOpen, enquiryData, fetchBOMHistory]);

  const fetchWarehouseStocks = useCallback(
    async (rawMaterialId) => {
      if (!rawMaterialId) return;
      setWarehouseLoading(true);
      try {
        const response = await axiosInstance.get(
          `${getApiEndpoint("")}/raw-materials/${rawMaterialId}/warehouse-stocks`,
        );
        if (response.data?.status === 200 && response.data?.data) {
          setWarehouseStocks(response.data.data);
        } else {
          setWarehouseStocks([]);
        }
      } catch (err) {
        setWarehouseStocks([]);
        console.error("Error fetching warehouse stocks:", err);
      } finally {
        setWarehouseLoading(false);
      }
    },
    [axiosInstance, getApiEndpoint],
  );

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
    setSelectedCombination(null);
    setBomQuantity("");
    setError("");
    setWarehouseStocks([]);
    setSelectedWarehouseStock(null);
    fetchWarehouseStocks(material.id);
  };

  const handleUpdateBOM = async () => {
    if (
      !selectedCombination ||
      !selectedWarehouseStock ||
      !bomQuantity ||
      parseFloat(bomQuantity) <= 0
    ) {
      setError("Please fill in all required fields");
      return;
    }

    if (parseFloat(bomQuantity) > selectedWarehouseStock.quantity) {
      setError(
        `Quantity cannot exceed available stock (${selectedWarehouseStock.quantity})`,
      );
      return;
    }

    if (!enquiryData?.productOrderHistoryId) {
      setError("Product order history ID is missing. Cannot update BOM.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const requestBody = {
        productOrderHistoryId: enquiryData.productOrderHistoryId,
        warehouseStockId: selectedWarehouseStock.id,
        quantity: parseFloat(bomQuantity),
        operationType: "SUBTRACT",
        remarks: bomRemarks || "",
      };

      const response = await axiosInstance.put(
        `${getApiEndpoint("")}/raw-material-stock/update-stock`,
        requestBody,
      );

      if (response.data?.status === 200) {
        if (onSuccess) onSuccess();
        handleClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update BOM");
      console.error("Error updating BOM:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedMaterial(null);
    setSelectedCombination(null);
    setBomQuantity("");
    setBomRemarks("");
    setError("");
    setSearchTerm("");
    setWarehouseStocks([]);
    setSelectedWarehouseStock(null);
    setBomHistory(null);
    setBomHistoryError("");
    onClose();
  };

  const filteredMaterials = rawMaterials.filter((material) => {
    const name = material?.name || "";
    const code = material?.materialCode || "";
    const searchTermLower = searchTerm.toLowerCase();
    return (
      name.toLowerCase().includes(searchTermLower) ||
      code.toLowerCase().includes(searchTermLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-[20px] shadow-2xl max-w-6xl w-full flex flex-col h-[calc(100vh-80px)] max-h-[900px]">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0 rounded-t-[20px]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Package className="text-blue-600" size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Add Materials to BOM
              </h2>
              <p className="text-sm text-gray-500">
                {enquiryData?.productHistories?.[0]?.product?.name ||
                  enquiryData?.stageName ||
                  "Product"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* BOM Stock History */}
        <div className="px-6 pt-4 shrink-0">
          <div className="border border-gray-200 rounded-lg bg-gray-50">
            <div className="px-4 py-2.5 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-700 tracking-wide">
                Warehouse Stock History
              </h3>
              {bomHistoryLoading && (
                <Loader className="animate-spin text-gray-400" size={14} />
              )}
            </div>
            <div className="px-4 pb-3">
              {bomHistoryError ? (
                <p className="text-xs text-red-600">{bomHistoryError}</p>
              ) : bomHistoryLoading && !bomHistory ? (
                <p className="text-xs text-gray-500">
                  Loading stock history...
                </p>
              ) : !bomHistory?.warehouseStockHistories?.length ? (
                <p className="text-xs text-gray-500">
                  No stock history found for this product order.
                </p>
              ) : (
                <div className="overflow-x-auto max-h-32 overflow-y-auto custom-scrollbar">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-1.5 pr-3 font-medium">Warehouse</th>
                        <th className="py-1.5 pr-3 font-medium">Qty Changed</th>
                        <th className="py-1.5 pr-3 font-medium">
                          Closing Balance
                        </th>
                        <th className="py-1.5 pr-3 font-medium">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bomHistory.warehouseStockHistories.map((entry) => (
                        <tr
                          key={entry.id}
                          className="border-b border-gray-100 last:border-0"
                        >
                          <td className="py-1.5 pr-3 text-gray-800">
                            {entry.warehouseName || "—"}
                          </td>
                          <td
                            className={`py-1.5 pr-3 font-medium ${
                              entry.quantityChanged < 0
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {entry.quantityChanged > 0 ? "+" : ""}
                            {entry.quantityChanged}
                          </td>
                          <td className="py-1.5 pr-3 text-gray-800">
                            {entry.closingBalance}
                          </td>
                          <td className="py-1.5 pr-3 text-gray-500">
                            {entry.remarks || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden p-6">
          <div className="h-full flex gap-6">
            {/* Step 1: Select Material */}
            <div className="w-1/2 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-4 shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    1
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    Select Raw Material
                  </h3>
                </div>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {filteredMaterials.length} items
                </span>
              </div>

              {/* Search */}
              <div className="relative mb-3 shrink-0">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search materials..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Materials List */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <div className="h-full overflow-y-auto pr-2 custom-scrollbar">
                  {loading && rawMaterials.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <Loader className="animate-spin mb-3" size={28} />
                      <p className="text-sm">Loading materials...</p>
                    </div>
                  ) : filteredMaterials.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <Package size={40} className="mb-3 opacity-30" />
                      <p className="text-sm">
                        {searchTerm
                          ? "No materials found"
                          : "No raw materials available"}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredMaterials.map((material) => {
                        const totalStock =
                          material.combinationStocks?.reduce(
                            (sum, c) => sum + (c.currentStock || 0),
                            0,
                          ) || 0;

                        return (
                          <button
                            key={material.id}
                            onClick={() => handleMaterialSelect(material)}
                            className={`w-full text-left p-3 rounded-lg border transition-all ${
                              selectedMaterial?.id === material.id
                                ? "border-blue-500 bg-blue-50 shadow-sm ring-2 ring-blue-200"
                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-gray-900 truncate">
                                  {material.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {material.materialCode}
                                </div>
                                {/* Variant chips preview */}
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {material.combinationStocks
                                    ?.slice(0, 3)
                                    .map((combo) => (
                                      <span
                                        key={combo.id}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200"
                                      >
                                        {combo.specificationValues
                                          ?.map((s) => s.value)
                                          .join(", ")}
                                      </span>
                                    ))}
                                  {(material.combinationStocks?.length || 0) >
                                    3 && (
                                    <span className="text-xs text-gray-400 px-1">
                                      +{material.combinationStocks.length - 3}{" "}
                                      more
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <div className="text-right">
                                  <div className="text-xs text-gray-500">
                                    Total Stock
                                  </div>
                                  <div
                                    className={`text-sm font-semibold ${totalStock > 0 ? "text-gray-900" : "text-red-500"}`}
                                  >
                                    {totalStock}
                                  </div>
                                </div>
                                {selectedMaterial?.id === material.id && (
                                  <ChevronRight
                                    className="text-blue-600"
                                    size={18}
                                  />
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-gray-200 shrink-0" />

            {/* Step 2: Select Variant & Quantity */}
            <div className="w-1/2 flex flex-col min-h-0">
              {selectedMaterial ? (
                <>
                  <div className="flex items-center gap-2 mb-4 shrink-0">
                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                      2
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Select Variant & Quantity
                    </h3>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto pr-2 custom-scrollbar">
                    {/* Material info strip */}
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
                      <Package size={18} className="text-blue-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-blue-900 truncate">
                          {selectedMaterial.name}
                        </div>
                        <div className="text-xs text-blue-600">
                          {selectedMaterial.materialCode}
                        </div>
                      </div>
                    </div>

                    {/* Combination Stocks */}
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Select Variant <span className="text-red-500">*</span>
                      </label>

                      {!selectedMaterial.combinationStocks?.length ? (
                        <div className="text-center p-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                          <Package
                            size={32}
                            className="mx-auto mb-2 opacity-30"
                          />
                          <p className="text-sm">No variants available</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {selectedMaterial.combinationStocks.map((combo) => (
                            <button
                              key={combo.id}
                              onClick={() => {
                                setSelectedCombination(combo);
                                setBomQuantity("");
                                setError("");
                                setSelectedWarehouseStock(null);
                              }}
                              className={`w-full text-left p-3 rounded-lg border transition-all ${
                                selectedCombination?.id === combo.id
                                  ? "border-green-500 bg-green-50 shadow-sm ring-2 ring-green-200"
                                  : combo.currentStock === 0
                                    ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                              }`}
                              disabled={combo.currentStock === 0}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap gap-1">
                                    {combo.specificationValues?.map((spec) => (
                                      <span
                                        key={spec.valueId}
                                        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white border border-gray-300 text-gray-700"
                                      >
                                        {spec.specificationName}:{" "}
                                        <span className="font-semibold ml-1">
                                          {spec.value}
                                        </span>
                                      </span>
                                    ))}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">
                                    Stock ID: {combo.stockId} · Unit:{" "}
                                    {combo.unit}
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <div className="text-xs text-gray-500">
                                    Available
                                  </div>
                                  <div
                                    className={`text-sm font-bold ${
                                      combo.currentStock > 0
                                        ? "text-green-600"
                                        : "text-red-500"
                                    }`}
                                  >
                                    {combo.currentStock}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Quantity & Remarks */}
                    {selectedCombination && (
                      <div className="bg-gray-50 rounded-lg p-4 space-y-4 border border-gray-200">
                        {/* Selected variant summary */}
                        <div className="flex items-center justify-between text-xs text-gray-600 bg-white rounded-md px-3 py-2 border border-gray-200">
                          <span>
                            {selectedCombination.specificationValues
                              ?.map((s) => `${s.specificationName}: ${s.value}`)
                              .join(" · ")}
                          </span>
                          <span className="font-semibold text-green-600 shrink-0 ml-2">
                            Stock: {selectedCombination.currentStock}{" "}
                            {selectedCombination.unit}
                          </span>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Warehouse <span className="text-red-500">*</span>
                          </label>
                          {warehouseLoading ? (
                            <div className="flex items-center gap-2 text-xs text-gray-500 px-3 py-2.5 border border-gray-300 rounded-lg bg-white">
                              <Loader className="animate-spin" size={14} />
                              Loading warehouses...
                            </div>
                          ) : warehouseStocks.length === 0 ? (
                            <div className="text-xs text-gray-500 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-100">
                              No warehouse stock found for this material
                            </div>
                          ) : (
                            <select
                              value={selectedWarehouseStock?.id ?? ""}
                              onChange={(e) => {
                                const stock = warehouseStocks.find(
                                  (w) => String(w.id) === e.target.value,
                                );
                                setSelectedWarehouseStock(stock || null);
                                setBomQuantity("");
                                setError("");
                              }}
                              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                            >
                              <option value="" disabled>
                                Select a warehouse
                              </option>
                              {warehouseStocks.map((stock) => (
                                <option key={stock.id} value={stock.id}>
                                  {stock.warehouseName}
                                  {stock.rackNumber
                                    ? ` · Rack ${stock.rackNumber}`
                                    : ""}{" "}
                                  · Stock: {stock.quantity}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Quantity <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="number"
                            value={bomQuantity}
                            onChange={(e) => setBomQuantity(e.target.value)}
                            max={selectedWarehouseStock?.quantity ?? 0}
                            min="0"
                            step="0.01"
                            placeholder="Enter quantity"
                            disabled={!selectedWarehouseStock}
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 mt-1.5">
                            Maximum available:{" "}
                            <span className="font-medium text-gray-700">
                              {selectedWarehouseStock
                                ? `${selectedWarehouseStock.quantity} ${selectedCombination.unit}`
                                : "Select a warehouse"}
                            </span>
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">
                            Remarks (Optional)
                          </label>
                          <textarea
                            value={bomRemarks}
                            onChange={(e) => setBomRemarks(e.target.value)}
                            placeholder="Add any additional notes..."
                            rows="3"
                            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <Package size={32} className="opacity-50" />
                  </div>
                  <p className="text-sm font-medium text-gray-500">
                    Select a raw material first
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Choose from the list on the left
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
              <span className="font-medium">⚠</span>
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between shrink-0 rounded-b-[20px]">
          <button
            onClick={handleClose}
            className="px-5 py-2.5 text-sm border border-gray-300 rounded-[20px] text-gray-700 hover:bg-gray-100 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdateBOM}
            disabled={
              !selectedCombination ||
              !selectedWarehouseStock ||
              !bomQuantity ||
              loading ||
              parseFloat(bomQuantity) <= 0 ||
              parseFloat(bomQuantity) > selectedWarehouseStock?.quantity
            }
            className={`px-5 py-2.5 text-sm rounded-[20px] font-medium flex items-center gap-2 transition-all ${
              !selectedCombination ||
              !selectedWarehouseStock ||
              !bomQuantity ||
              loading ||
              parseFloat(bomQuantity) <= 0 ||
              parseFloat(bomQuantity) > selectedWarehouseStock?.quantity
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow"
            }`}
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={16} />
                Adding...
              </>
            ) : (
              <>
                <Plus size={16} />
                Add to BOM
              </>
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>
    </div>
  );
};

export default EnquiryBOMModal;
